import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, compressImage, formatApiErrorDetail } from "@/lib/api";
import { PRIORITY_LABELS } from "@/lib/labels";
import { PGA_LEVEL_LABELS, PGA_LEVEL_COLORS } from "@/components/PgaExtras";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Camera, Check, X, Minus, ChevronRight, CheckCircle2, Wheat, Trash2, Bug, Plus, Thermometer, Droplets } from "lucide-react";

function PhotoStrip({ photos, onRemove }) {
  if (!photos?.length) return null;
  return (
    <div className="flex flex-wrap gap-2 mt-2">
      {photos.map((p, i) => (
        <div key={i} className="relative group">
          <img src={p} alt={`evidência ${i + 1}`} className="h-14 w-14 rounded-md object-cover border border-border" />
          <button onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 h-4 w-4 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
            <X size={10} />
          </button>
        </div>
      ))}
    </div>
  );
}

function PhotoInput({ id, onFiles, label = "Adicionar fotos" }) {
  return (
    <label htmlFor={id} className="inline-flex items-center gap-1.5 text-xs text-primary cursor-pointer hover:underline">
      <Camera size={13} /> {label}
      <input id={id} type="file" accept="image/*" multiple className="hidden"
        onChange={(e) => { onFiles(e.target.files); e.target.value = ""; }} />
    </label>
  );
}

export default function PgaMonitoringFlow({ structures, types, startIndex = 0, onExit, onFinished }) {
  const [idx, setIdx] = useState(startIndex);
  const [answers, setAnswers] = useState({});
  const [captures, setCaptures] = useState([]);
  const [temperature, setTemperature] = useState("");
  const [humidity, setHumidity] = useState("");
  const [generalPhotos, setGeneralPhotos] = useState([]);
  const [notes, setNotes] = useState("");
  const [species, setSpecies] = useState([]);
  const [gps, setGps] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [results, setResults] = useState([]);
  const [done, setDone] = useState(false);
  const structure = structures[idx];
  const checklist = (types.find((t) => t.id === structure.type_id)?.checklist) || [];

  useEffect(() => {
    api.get("/pga/species").then(({ data }) => setSpecies(data)).catch(() => {});
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => setGps({ lat: pos.coords.latitude, lng: pos.coords.longitude, accuracy: pos.coords.accuracy }),
        () => {},
        { enableHighAccuracy: true, timeout: 8000 }
      );
    }
  }, []);

  const setItem = (key, patch) => setAnswers((a) => ({ ...a, [key]: { ...(a[key] || {}), ...patch } }));
  const setCap = (i, patch) => setCaptures((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const emptyCapture = () => ({ species_id: "", species_name: "", quantity: 1, trap_type: "", location: "", detail: "", photos: [] });

  const addPhotos = async (files, apply) => {
    const arr = [];
    for (const f of Array.from(files || [])) arr.push(await compressImage(f));
    apply(arr);
  };

  const submit = async () => {
    const items = [];
    for (const t of checklist) {
      const a = answers[t.key] || {};
      if (t.required && !a.status) return toast.error(`Responda o item: ${t.label}`);
      items.push({ key: t.key, label: t.label, status: a.status || "nao_aplicavel", severity: a.severity || t.severity || "media", notes: a.notes || "", photos: a.photos || [] });
    }
    for (const c of items) {
      const t = checklist.find((x) => x.key === c.key) || {};
      if (t.photo_required && c.photos.length < 1)
        return toast.error(`Foto obrigatória para a pergunta: ${c.label}`);
      if (c.status === "nao_conforme" && c.photos.length < 1)
        return toast.error(`Evidência fotográfica obrigatória no item não conforme: ${c.label}`);
    }
    for (const c of captures) {
      const sp = species.find((s) => s.id === c.species_id);
      if (!c.species_id) return toast.error("Selecione a espécie da praga capturada");
      if (sp?.requires_detail && !c.species_name.trim() && !c.detail.trim()) return toast.error(`Informe a espécie/detalhe para "${sp.name}"`);
      if (!c.quantity || c.quantity < 1) return toast.error("Quantidade capturada deve ser no mínimo 1");
      if (!c.photos.length) return toast.error("Foto obrigatória para cada registro de captura");
    }
    const totalPhotos = generalPhotos.length + items.reduce((s, c) => s + c.photos.length, 0) + captures.reduce((s, c) => s + c.photos.length, 0);
    if (totalPhotos === 0) return toast.error("Anexe pelo menos 1 foto da estrutura");
    setSubmitting(true);
    try {
      const { data } = await api.post(`/pga/structures/${structure.id}/monitorings`, {
        checklist: items,
        captures,
        temperature: temperature === "" ? null : parseFloat(temperature),
        humidity: humidity === "" ? null : parseFloat(humidity),
        general_photos: generalPhotos, notes, gps,
      });
      setResults((r) => [...r, { name: structure.name, type: structure.type_name, index: data.infestation_index, level: data.infestation_level, ncs: data.nc_count, occurrence: data.occurrence, captures: captures.reduce((s, c) => s + c.quantity, 0) }]);
      toast.success(`"${structure.name}" monitorada — índice ${data.infestation_index}/100 (${PGA_LEVEL_LABELS[data.infestation_level]})${data.occurrence ? ` · Ocorrência ${data.occurrence}` : ""}`);
      if (idx + 1 < structures.length) {
        setIdx(idx + 1);
        setAnswers({}); setCaptures([]); setTemperature(""); setHumidity(""); setGeneralPhotos([]); setNotes("");
        window.scrollTo({ top: 0, behavior: "smooth" });
      } else {
        setDone(true);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-2xl mx-auto space-y-6" data-testid="pga-flow-summary">
        <div className="text-center pt-6">
          <CheckCircle2 size={44} className="mx-auto text-emerald-400" />
          <h2 className="font-heading text-2xl font-bold mt-3">Monitoramento concluído</h2>
          <p className="text-sm text-muted-foreground mt-1">
            {results.length} estrutura(s) monitorada(s) · {results.reduce((s, r) => s + r.captures, 0)} praga(s) capturada(s) · {results.filter((r) => r.occurrence).length} ocorrência(s) gerada(s)
          </p>
        </div>
        <div className="space-y-2">
          {results.map((r, i) => (
            <Card key={i} className="p-4 flex items-center justify-between gap-3 border-border bg-card" data-testid={`pga-summary-${i}`}>
              <div className="flex items-center gap-3 min-w-0">
                <Wheat size={16} className="text-yellow-500 shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{r.name} <span className="text-xs text-muted-foreground">({r.type})</span></p>
                  {r.occurrence && <p className="text-xs text-muted-foreground truncate">Ocorrência: {r.occurrence}</p>}
                </div>
              </div>
              <Badge variant="outline" className={PGA_LEVEL_COLORS[r.level]}>
                {r.index}/100 · {PGA_LEVEL_LABELS[r.level]} · {r.ncs} NC{r.captures > 0 ? ` · ${r.captures} praga(s)` : ""}
              </Badge>
            </Card>
          ))}
        </div>
        <Button className="w-full" onClick={onFinished} data-testid="pga-flow-finish-btn">Encerrar e voltar ao painel</Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-5" data-testid="pga-monitoring-flow">
      <button onClick={onExit} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="pga-flow-exit-btn">
        <ArrowLeft size={15} /> Sair do monitoramento
      </button>

      <div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <h2 className="font-heading text-xl font-bold flex items-center gap-2" data-testid="pga-flow-structure-name">
            <Wheat size={18} className="text-yellow-500" /> {structure.name}
            <span className="text-sm text-muted-foreground font-normal">({structure.type_name}{structure.code ? ` · ${structure.code}` : ""})</span>
          </h2>
          <Badge variant="outline" data-testid="pga-flow-progress">Estrutura {idx + 1} de {structures.length}</Badge>
        </div>
        <div className="h-1.5 rounded-full bg-muted mt-3">
          <div className="h-full rounded-full bg-primary transition-all" style={{ width: `${(idx / structures.length) * 100}%` }} />
        </div>
      </div>

      <div className="space-y-3">
        {checklist.map((t) => {
          const a = answers[t.key] || {};
          return (
            <Card key={t.key} className="p-4 border-border bg-card" data-testid={`pga-check-${t.key}`}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <p className="text-sm font-medium flex items-center gap-1.5">
                  {t.label}
                  {t.required && (
                    <span className="text-[10px] font-semibold text-primary bg-primary/10 rounded px-1.5 py-0.5" data-testid={`pga-required-badge-${t.key}`}>Obrigatória</span>
                  )}
                  {t.photo_required && (
                    <span className="text-[10px] font-semibold text-amber-600 bg-amber-500/10 rounded px-1.5 py-0.5" data-testid={`pga-photo-badge-${t.key}`}>📸 Foto</span>
                  )}
                </p>
                <div className="flex gap-1.5">
                  <button onClick={() => setItem(t.key, { status: "conforme" })}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors ${a.status === "conforme" ? "bg-emerald-500/20 text-emerald-400 border-emerald-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`pga-check-${t.key}-conforme`}>
                    <Check size={12} /> Conforme
                  </button>
                  <button onClick={() => setItem(t.key, { status: "nao_conforme", severity: a.severity || t.severity })}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors ${a.status === "nao_conforme" ? "bg-red-500/20 text-red-400 border-red-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`pga-check-${t.key}-nc`}>
                    <X size={12} /> Não conforme
                  </button>
                  <button onClick={() => setItem(t.key, { status: "nao_aplicavel" })}
                    className={`px-2.5 py-1.5 rounded-md text-xs font-semibold border flex items-center gap-1 transition-colors ${a.status === "nao_aplicavel" ? "bg-slate-500/20 text-slate-400 border-slate-500/40" : "border-border text-muted-foreground hover:bg-accent"}`}
                    data-testid={`pga-check-${t.key}-na`}>
                    <Minus size={12} /> N/A
                  </button>
                </div>
              </div>
              {t.photo_required && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2.5" data-testid={`pga-photo-required-panel-${t.key}`}>
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="text-xs text-primary font-semibold flex items-center gap-1.5">
                      <Camera size={13} /> Foto obrigatória *
                    </span>
                    <PhotoInput id={`pga-photo-req-${t.key}`} onFiles={(f) => addPhotos(f, (arr) => setItem(t.key, { photos: [...(a.photos || []), ...arr].slice(0, 10) }))} />
                  </div>
                  <PhotoStrip photos={a.photos} onRemove={(i) => setItem(t.key, { photos: a.photos.filter((_, j) => j !== i) })} />
                </div>
              )}
              {a.status === "nao_conforme" && (
                <div className="mt-3 pt-3 border-t border-border/60 space-y-2.5" data-testid={`pga-nc-panel-${t.key}`}>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-muted-foreground">Severidade:</span>
                      <Select value={a.severity || t.severity} onValueChange={(v) => setItem(t.key, { severity: v })}>
                        <SelectTrigger className="h-8 w-28 text-xs" data-testid={`pga-nc-severity-${t.key}`}><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {Object.entries(PRIORITY_LABELS).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <PhotoInput id={`pga-photo-${t.key}`} label="Foto da evidência (obrigatória) *"
                      onFiles={(f) => addPhotos(f, (arr) => setItem(t.key, { photos: [...(a.photos || []), ...arr].slice(0, 10) }))} />
                  </div>
                  <Textarea rows={2} placeholder="Observações da não conformidade..." className="resize-none text-sm"
                    value={a.notes || ""} onChange={(e) => setItem(t.key, { notes: e.target.value })} data-testid={`pga-nc-notes-${t.key}`} />
                  <PhotoStrip photos={a.photos} onRemove={(i) => setItem(t.key, { photos: a.photos.filter((_, j) => j !== i) })} />
                </div>
              )}
            </Card>
          );
        })}
      </div>

      <Card className="p-4 border-border bg-card space-y-3" data-testid="pga-captures-section">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <p className="text-sm font-medium flex items-center gap-2">
            <Bug size={15} className="text-yellow-500" /> Capturas de pragas nos pontos de monitoramento
          </p>
          <button onClick={() => setCaptures((cs) => [...cs, emptyCapture()])} className="text-xs text-primary hover:underline flex items-center gap-1" data-testid="pga-capture-add-btn">
            <Plus size={12} /> Registrar captura
          </button>
        </div>
        {captures.length === 0 && <p className="text-xs text-muted-foreground">Nenhuma captura registrada nesta estrutura.</p>}
        {captures.map((c, i) => {
          const sp = species.find((s) => s.id === c.species_id);
          return (
            <div key={i} className="rounded-md border border-yellow-500/30 bg-yellow-500/5 p-3 space-y-2.5" data-testid={`pga-capture-${i}`}>
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-yellow-500 uppercase tracking-wide">Captura {i + 1}</p>
                <button onClick={() => setCaptures((cs) => cs.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-red-400" data-testid={`pga-capture-remove-${i}`}>
                  <Trash2 size={13} />
                </button>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
                <Select value={c.species_id} onValueChange={(v) => { const s = species.find((x) => x.id === v); setCap(i, { species_id: v, species_name: s && !s.requires_detail ? s.name : "" }); }}>
                  <SelectTrigger className="h-9" data-testid={`pga-capture-species-${i}`}><SelectValue placeholder="Espécie *" /></SelectTrigger>
                  <SelectContent>
                    {species.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}{s.scientific_name ? ` (${s.scientific_name})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="number" min={1} value={c.quantity} onChange={(e) => setCap(i, { quantity: Math.max(1, parseInt(e.target.value || "1")) })}
                  className="h-9" placeholder="Quantidade *" data-testid={`pga-capture-quantity-${i}`} />
                <Input value={c.trap_type} onChange={(e) => setCap(i, { trap_type: e.target.value })}
                  className="h-9" placeholder="Tipo de armadilha" data-testid={`pga-capture-trap-${i}`} />
              </div>
              <Input value={c.location} onChange={(e) => setCap(i, { location: e.target.value })}
                className="h-9" placeholder="Local do ponto (ex.: base do silo, moega)" data-testid={`pga-capture-location-${i}`} />
              {sp?.requires_detail && (
                <Input value={c.species_name} onChange={(e) => setCap(i, { species_name: e.target.value })}
                  placeholder="Informe a espécie *" className="h-9" data-testid={`pga-capture-custom-species-${i}`} />
              )}
              <PhotoInput id={`pga-capture-photo-${i}`} label="Foto da captura (obrigatória) *"
                onFiles={(f) => addPhotos(f, (arr) => setCap(i, { photos: [...c.photos, ...arr].slice(0, 10) }))} />
              <PhotoStrip photos={c.photos} onRemove={(j) => setCap(i, { photos: c.photos.filter((_, k) => k !== j) })} />
            </div>
          );
        })}
        {captures.length > 0 && (
          <span className="text-[11px] text-muted-foreground block" data-testid="pga-gps-info">
            {gps ? `GPS registrado: ${gps.lat.toFixed(4)}, ${gps.lng.toFixed(4)}` : "GPS não disponível (permissão negada ou indisponível)"}
          </span>
        )}
      </Card>

      <Card className="p-4 border-border bg-card space-y-3" data-testid="pga-conditions-section">
        <p className="text-sm font-medium">Condições ambientais da estrutura</p>
        <div className="grid grid-cols-2 gap-3">
          <div className="relative">
            <Thermometer size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-orange-400" />
            <Input type="number" step="0.1" value={temperature} onChange={(e) => setTemperature(e.target.value)}
              className="h-9 pl-8" placeholder="Temperatura (°C)" data-testid="pga-temperature-input" />
          </div>
          <div className="relative">
            <Droplets size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-sky-400" />
            <Input type="number" step="0.1" value={humidity} onChange={(e) => setHumidity(e.target.value)}
              className="h-9 pl-8" placeholder="Umidade relativa (%)" data-testid="pga-humidity-input" />
          </div>
        </div>
      </Card>

      <Card className="p-4 border-border bg-card space-y-2.5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-sm font-medium">Fotos gerais da estrutura</p>
          <PhotoInput id="pga-general-photos" onFiles={(f) => addPhotos(f, (arr) => setGeneralPhotos((p) => [...p, ...arr].slice(0, 10)))} />
        </div>
        <PhotoStrip photos={generalPhotos} onRemove={(i) => setGeneralPhotos((p) => p.filter((_, j) => j !== i))} />
        <Textarea rows={2} placeholder="Observações gerais do monitoramento..." className="resize-none text-sm"
          value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="pga-flow-notes" />
      </Card>

      <Button className="w-full" size="lg" onClick={submit} disabled={submitting} data-testid="pga-flow-submit-btn">
        {submitting ? "Registrando..." : idx + 1 < structures.length ? (
          <>Concluir estrutura e avançar para a próxima <ChevronRight size={16} className="ml-1" /></>
        ) : (
          <>Concluir última estrutura e finalizar <CheckCircle2 size={16} className="ml-1" /></>
        )}
      </Button>
    </div>
  );
}
